import type { ProjectContextDocument } from '@protoss/shared';
import * as React from 'react';
import { useContextDocs } from '../hooks/useContextDocs';
import { ContextDocEditor } from './ContextDocEditor';
import { ContextDocList } from './ContextDocList';

type PaneMode = 'list' | 'creating' | 'editing';

export interface ContextDocsPaneHandle {
  startNew: () => void;
  cancelEdit: () => void;
}

export interface ContextDocsPaneProps {
  projectUuid: string;
  /** System page scope (e.g. 'repos', 'commits') - used to tag context docs to a source system */
  scope?: string;
  /** Called when editor mode opens or closes */
  onEditorModeChange?: (isEditing: boolean) => void;
}

export const ContextDocsPane = React.forwardRef<ContextDocsPaneHandle, ContextDocsPaneProps>(
  function ContextDocsPane({ projectUuid, scope, onEditorModeChange }, ref) {
    const { documents, status, error, create, update, remove } = useContextDocs(projectUuid, scope);

    const [mode, setMode] = React.useState<PaneMode>('list');
    const [activeDoc, setActiveDoc] = React.useState<ProjectContextDocument | null>(null);
    const [saving, setSaving] = React.useState(false);
    const [saveSucceeded, setSaveSucceeded] = React.useState(false);
    const [saveError, setSaveError] = React.useState<string | null>(null);
    const saveSuccessTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

    React.useImperativeHandle(ref, () => ({
      startNew: () => handleNew(),
      cancelEdit: () => handleCancel(),
    }));

    const handleNew = () => {
      setActiveDoc(null);
      setMode('creating');
      setSaveError(null);
      setSaveSucceeded(false);
      onEditorModeChange?.(true);
    };

    const handleSelect = (doc: ProjectContextDocument) => {
      setActiveDoc(doc);
      setMode('editing');
      setSaveError(null);
      setSaveSucceeded(false);
      onEditorModeChange?.(true);
    };

    const handleCancel = () => {
      setMode('list');
      setActiveDoc(null);
      setSaveError(null);
      setSaveSucceeded(false);
      if (saveSuccessTimerRef.current) clearTimeout(saveSuccessTimerRef.current);
      onEditorModeChange?.(false);
    };

    const handleSave = async (
      title: string,
      body: Record<string, unknown> | null,
      contentText: string
    ) => {
      setSaving(true);
      setSaveError(null);
      try {
        if (mode === 'creating') {
          const newDoc = await create({
            title,
            body: body ?? undefined,
            contentText: contentText || undefined,
          });
          setActiveDoc(newDoc);
          setMode('editing');
        } else if (mode === 'editing' && activeDoc) {
          const updated = await update(activeDoc.uuid, {
            title,
            body: body ?? undefined,
            contentText: contentText || undefined,
          });
          setActiveDoc(updated);
        }
        // Flash "Saved" for 2.5 seconds
        setSaveSucceeded(true);
        if (saveSuccessTimerRef.current) clearTimeout(saveSuccessTimerRef.current);
        saveSuccessTimerRef.current = setTimeout(() => setSaveSucceeded(false), 2500);
      } catch (err) {
        setSaveError(err instanceof Error ? err.message : 'Save failed');
      } finally {
        setSaving(false);
      }
    };

    const handleDelete = async (doc: ProjectContextDocument) => {
      try {
        await remove(doc.uuid);
        if (activeDoc?.uuid === doc.uuid) {
          setActiveDoc(null);
          setMode('list');
        }
      } catch {
        // Errors surface via list state; nothing to do here
      }
    };

    const isEditorMode = mode === 'creating' || mode === 'editing';

    return (
      <div className="ctx-docs-pane">
        {/* Error banner */}
        {(error || saveError) && (
          <div className="ctx-docs-pane__error" role="alert">
            {saveError ?? error}
          </div>
        )}

        {/* Content */}
        <div className="ctx-docs-pane__content">
          {mode === 'list' && (
            <ContextDocList
              documents={documents}
              loading={status === 'loading'}
              onSelect={handleSelect}
              onDelete={handleDelete}
            />
          )}

          {isEditorMode && (
            <ContextDocEditor
              document={activeDoc}
              saving={saving}
              saveSucceeded={saveSucceeded}
              onSave={handleSave}
              onCancel={handleCancel}
            />
          )}
        </div>
      </div>
    );
  }
);
